import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/AppLayout";
import ExportMenu from "@/components/ExportMenu";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Wallet, TrendingUp, AlertTriangle, PiggyBank, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => (n || 0).toLocaleString("fr-FR").replace(/,/g, " ");
const REGIMES = ["Hiérarchique", "Tutelle", "Déconcentration"];

const ALERTE = {
  ok: { label: "OK", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  vigilance: { label: "Vigilance", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  dépassement: { label: "Dépassement", cls: "bg-red-50 text-red-700 border-red-200" },
};

const EMPTY = { entite_nom: "", regime: "Hiérarchique", annee: new Date().getFullYear(), alloue: 0, engage: 0, consomme: 0 };

function Kpi({ icon: Icon, label, value, accent, suffix = "", testid }) {
  return (
    <Card className="rounded-sm border-border shadow-none p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{label}</p>
          <p className="font-heading text-2xl font-bold mt-2 text-navy truncate">{value}<span className="text-sm text-muted-foreground ml-1">{suffix}</span></p>
        </div>
        <div className="h-9 w-9 flex items-center justify-center rounded-sm shrink-0" style={{ background: `${accent}14`, color: accent }}>
          <Icon className="h-5 w-5" strokeWidth={1.5} />
        </div>
      </div>
    </Card>
  );
}

export default function Budgets() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [annee, setAnnee] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const canEdit = user?.role === "admin" || user?.role === "sg";

  const load = useCallback(async () => {
    const q = annee ? `?annee=${annee}` : "";
    const { data } = await api.get(`/budgets${q}`);
    setData(data);
    if (annee === null && data.annees.length) setAnnee(data.annees[0]);
  }, [annee]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY, annee: annee || EMPTY.annee }); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ entite_nom: b.entite_nom, regime: b.regime, annee: b.annee, alloue: b.alloue, engage: b.engage, consomme: b.consomme }); setOpen(true); };

  const save = async () => {
    if (!form.entite_nom) { toast.error("Nom de l'entité requis"); return; }
    const payload = { ...form, annee: Number(form.annee), alloue: Number(form.alloue), engage: Number(form.engage), consomme: Number(form.consomme) };
    try {
      if (editing) await api.put(`/budgets/${editing.id}`, payload);
      else await api.post("/budgets", payload);
      toast.success(editing ? "Ligne mise à jour" : "Ligne budgétaire créée");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Supprimer la ligne « ${b.entite_nom} » ?`)) return;
    await api.delete(`/budgets/${b.id}`);
    toast.success("Ligne supprimée"); load();
  };

  if (!data) return <div className="p-8 text-muted-foreground">Chargement…</div>;
  const s = data.summary;

  return (
    <>
      <PageHeader
        title="Suivi des budgets & subventions"
        subtitle="Subventions de l'État — ministère, établissements sous tutelle et délégations provinciales"
        actions={
          <div className="flex items-center gap-3">
            {data.annees.length > 0 && (
              <Select value={String(annee)} onValueChange={(v) => setAnnee(Number(v))}>
                <SelectTrigger className="rounded-sm w-28" data-testid="budget-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {data.annees.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <ExportMenu basePath="/budgets/export" filename="budgets_sg_podc" extraQuery={annee ? `annee=${annee}` : ""} testid="budget-export" />
            {canEdit && (
              <Button onClick={openCreate} className="rounded-sm bg-navy hover:bg-navy-dark" data-testid="new-budget-button">
                <Plus className="h-4 w-4 mr-1.5" /> Nouvelle ligne
              </Button>
            )}
          </div>
        }
      />
      <div className="p-8 space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi icon={Wallet} label="Subvention allouée" value={fmt(s.alloue)} suffix="FCFA" accent="#002664" testid="kpi-alloue" />
          <Kpi icon={PiggyBank} label="Consommé" value={fmt(s.consomme)} suffix="FCFA" accent="#0f766e" testid="kpi-consomme" />
          <Kpi icon={TrendingUp} label="Taux d'exécution" value={`${s.taux_execution}`} suffix="%" accent="#b45309" testid="kpi-taux" />
          <Kpi icon={AlertTriangle} label="Dépassements" value={s.depassements} suffix={`/ ${s.count}`} accent="#DC2626" testid="kpi-depassements" />
        </div>

        <Card className="rounded-sm border-border shadow-none overflow-hidden" data-testid="budget-table">
          <div className="overflow-x-auto scroll-thin">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3 font-semibold">Entité</th>
                  <th className="px-4 py-3 font-semibold">Régime</th>
                  <th className="px-4 py-3 font-semibold text-right">Alloué (FCFA)</th>
                  <th className="px-4 py-3 font-semibold text-right">Consommé</th>
                  <th className="px-4 py-3 font-semibold text-right">Solde</th>
                  <th className="px-4 py-3 font-semibold w-48">Exécution</th>
                  <th className="px-4 py-3 font-semibold">Alerte</th>
                  {canEdit && <th className="px-4 py-3" />}
                </tr>
              </thead>
              <tbody>
                {data.budgets.map((b) => {
                  const al = ALERTE[b.alerte] || ALERTE.ok;
                  const pct = Math.min(b.taux_execution, 120);
                  const barColor = b.depassement ? "#DC2626" : b.taux_execution >= 90 ? "#D97706" : "#059669";
                  return (
                    <tr key={b.id} className="border-t border-border hover:bg-slate-50/60" data-testid={`budget-row-${b.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-800">{b.entite_nom}</td>
                      <td className="px-4 py-3 text-slate-600">{b.regime}</td>
                      <td className="px-4 py-3 text-right font-mono">{fmt(b.alloue)}</td>
                      <td className="px-4 py-3 text-right font-mono">{fmt(b.consomme)}</td>
                      <td className={`px-4 py-3 text-right font-mono ${b.solde < 0 ? "text-red-600" : "text-slate-700"}`}>{fmt(b.solde)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: barColor }} />
                          </div>
                          <span className="text-[11px] font-mono text-slate-500 w-10 text-right">{b.taux_execution}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded-sm border px-2 py-0.5 text-[10px] uppercase tracking-wide font-semibold ${al.cls}`}>{al.label}</span>
                      </td>
                      {canEdit && (
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 justify-end">
                            <button onClick={() => openEdit(b)} className="p-1.5 rounded-sm hover:bg-slate-100 text-slate-500" data-testid={`edit-budget-${b.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                            {user?.role === "admin" && <button onClick={() => remove(b)} className="p-1.5 rounded-sm hover:bg-red-50 text-red-500" data-testid={`delete-budget-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm" data-testid="budget-dialog">
          <DialogHeader><DialogTitle className="font-heading text-navy">{editing ? "Modifier la ligne budgétaire" : "Nouvelle ligne budgétaire"}</DialogTitle>
            <DialogDescription>Subvention de l'État : montants alloué, engagé et consommé par entité et par année.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Entité</Label>
              <Input value={form.entite_nom} onChange={(e) => setForm({ ...form, entite_nom: e.target.value })} className="rounded-sm" data-testid="budget-entite" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Régime</Label>
                <Select value={form.regime} onValueChange={(v) => setForm({ ...form, regime: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="budget-regime"><SelectValue /></SelectTrigger>
                  <SelectContent>{REGIMES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Année</Label>
                <Input type="number" value={form.annee} onChange={(e) => setForm({ ...form, annee: e.target.value })} className="rounded-sm" data-testid="budget-annee" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Alloué</Label>
                <Input type="number" value={form.alloue} onChange={(e) => setForm({ ...form, alloue: e.target.value })} className="rounded-sm" data-testid="budget-alloue" />
              </div>
              <div className="space-y-1.5">
                <Label>Engagé</Label>
                <Input type="number" value={form.engage} onChange={(e) => setForm({ ...form, engage: e.target.value })} className="rounded-sm" data-testid="budget-engage" />
              </div>
              <div className="space-y-1.5">
                <Label>Consommé</Label>
                <Input type="number" value={form.consomme} onChange={(e) => setForm({ ...form, consomme: e.target.value })} className="rounded-sm" data-testid="budget-consomme" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="rounded-sm bg-navy hover:bg-navy-dark" data-testid="budget-submit">{editing ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
